/**
 * Timezone-Utilities für die Arenaris Booking-Anwendung
 * Die API sendet und verarbeitet alle Zeiten in Berlin-Zeit ohne Zeitzone
 * Format: YYYY-MM-DDTHH:mm:ss
 */

/**
 * Deutsche Zeitzone (Europe/Berlin)
 * Berücksichtigt automatisch Sommer-/Winterzeit
 */
export const GERMAN_TIMEZONE = 'Europe/Berlin';

/**
 * Konvertiert Zeitstring vom Backend (Berlin-Zeit ohne Zeitzone) in Date-Objekt
 * Das Backend sendet Zeiten im Format YYYY-MM-DDTHH:mm:ss (ohne Zeitzone)
 * Diese werden als lokale Zeit interpretiert
 * @param {string} timeString - Zeitstring vom Backend im Format YYYY-MM-DDTHH:mm:ss
 * @returns {Date|null} Date-Objekt oder null bei ungültigen Daten
 */
export function parseBackendDateTime(timeString) {
  if (!timeString || typeof timeString !== 'string') return null;
  
  try {
    // Entferne eventuelles 'Z' am Ende (für Rückwärtskompatibilität)
    let cleanString = timeString.trim();
    
    // Wenn bereits ISO-Format mit Z, entferne das Z für lokale Interpretation
    if (cleanString.endsWith('Z') && cleanString.includes('T')) {
      cleanString = cleanString.slice(0, -1);
    }
    
    // Ersetze Leerzeichen durch T für ISO-Format
    if (cleanString.includes(' ') && !cleanString.includes('T')) {
      cleanString = cleanString.replace(' ', 'T');
    }
    
    // Parse als lokale Zeit (keine Zeitzone = lokale Interpretation)
    const date = new Date(cleanString);
    
    // Prüfe auf ungültiges Datum
    if (isNaN(date.getTime())) {
      console.warn('Ungültiger Zeitstring vom Backend:', timeString);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Fehler beim Parsen des Backend-Zeitstrings:', timeString, error);
    return null;
  }
}

/**
 * Formatiert lokales Date-Objekt für Backend (Berlin-Zeit ohne Zeitzone)
 * Das Backend erwartet Zeiten im Format YYYY-MM-DDTHH:mm:ss (ohne Zeitzone)
 * @param {Date} localDate - Lokales Date-Objekt
 * @returns {string} Zeitstring im Format YYYY-MM-DDTHH:mm:ss
 */
export function formatDateTimeForBackend(localDate) {
  if (!localDate || !(localDate instanceof Date)) return '';
  
  try {
    // Prüfe auf ungültiges Datum
    if (isNaN(localDate.getTime())) {
      console.warn('Ungültiges Date-Objekt für Backend-Formatierung:', localDate);
      return '';
    }
    
    // Formatiere als lokale Zeit im Format YYYY-MM-DDTHH:mm:ss (ohne Zeitzone)
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Fehler beim Formatieren für Backend:', localDate, error);
    return '';
  }
}

/**
 * Formatiert ein Date-Objekt für die deutsche Anzeige
 * @param {Date} date - Date-Objekt
 * @param {Object} options - Formatierungsoptionen
 * @returns {string} Formatierte Zeitstring
 */
export function formatGermanDateTime(date, options = {}) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  
  const defaultOptions = {
    timeZone: GERMAN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  try {
    return date.toLocaleString('de-DE', defaultOptions);
  } catch (error) {
    console.error('Fehler beim Formatieren der deutschen Zeit:', date, error);
    return date.toLocaleString('de-DE');
  }
}

/**
 * Prüft ob ein Zeitstring ein gültiges Backend-Format hat
 * Erwartetes Format: YYYY-MM-DDTHH:mm:ss (ohne Zeitzone)
 * @param {string} timeString - Zu prüfender Zeitstring
 * @returns {boolean} true wenn gültig
 */
export function isValidBackendTimeString(timeString) {
  if (!timeString || typeof timeString !== 'string') return false;
  
  // Prüfe Format: YYYY-MM-DDTHH:MM:SS (ohne Zeitzone)
  const formatRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  // Prüfe altes Format mit Leerzeichen: YYYY-MM-DD HH:MM:SS (für Rückwärtskompatibilität)
  const oldFormatRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
  // Prüfe Format mit Z am Ende (für Rückwärtskompatibilität)
  const withZRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
  
  if (!formatRegex.test(timeString) && !oldFormatRegex.test(timeString) && !withZRegex.test(timeString)) {
    return false;
  }
  
  // Prüfe ob das Datum gültig ist
  const parsed = parseBackendDateTime(timeString);
  return parsed !== null;
}

/**
 * Debugging-Hilfsfunktion: Zeigt Zeitzone-Informationen an
 * @param {Date} date - Date-Objekt zum Debuggen
 */
export function debugTimezone(date) {
  if (!date || !(date instanceof Date)) {
    console.log('Debug Timezone: Ungültiges Date-Objekt', date);
    return;
  }
  
  console.log('=== Timezone Debug ===');
  console.log('Original Date:', date);
  console.log('UTC String:', date.toISOString());
  console.log('German Local:', formatGermanDateTime(date));
  console.log('Local Time String:', date.toLocaleString('de-DE'));
  console.log('Timezone Offset (min):', date.getTimezoneOffset());
  console.log('Backend Format (Berlin ohne Zeitzone):', formatDateTimeForBackend(date));
  console.log('======================');
}
